import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Mail, MapPin, Phone, Loader2, CheckCircle2 } from "lucide-react";
import AcceptTerms from "../components/AcceptTerms";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", subject: "General Inquiry", message: "" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/contact", form);
      setDone(true);
      setForm({ name: "", email: "", organization: "", subject: "General Inquiry", message: "" });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24" data-testid="contact-page">
      <div className="py-12">
        <div className="overline text-[var(--red)] mb-4">Contact</div>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.05]">
          Let&apos;s discuss your <span className="text-[var(--red)]">evaluation needs.</span>
        </h1>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-8">
          <div className="card-dark p-7" data-testid="contact-info">
            <div className="overline mb-4">Direct</div>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3 text-[var(--text-2)]"><Mail className="w-4 h-4 mt-0.5 text-[var(--red)]" /> contact@publichealthmasters.org</li>
              <li className="flex items-start gap-3 text-[var(--text-2)]"><MapPin className="w-4 h-4 mt-0.5 text-[var(--red)]" /> 8301 W Flamingo Rd, Unit 1099<br/>Las Vegas, NV 89147</li>
              <li className="flex items-start gap-3 text-[var(--text-2)]"><Phone className="w-4 h-4 mt-0.5 text-[var(--red)]" /> 725-314-8760</li>
            </ul>
          </div>
          <div className="card-dark p-7">
            <div className="overline mb-3">Response time</div>
            <p className="text-sm text-[var(--text-2)] leading-relaxed">We respond to inquiries within 1 business day. For grant deadlines, please mark &quot;Urgent&quot; in the subject.</p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={onSubmit} className="card-dark p-8 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="overline mb-2 block">Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" data-testid="contact-name" />
              </div>
              <div>
                <label className="overline mb-2 block">Email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dark" data-testid="contact-email" />
              </div>
            </div>
            <div>
              <label className="overline mb-2 block">Organization</label>
              <input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="input-dark" data-testid="contact-org" />
            </div>
            <div>
              <label className="overline mb-2 block">Subject</label>
              <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="input-dark" data-testid="contact-subject">
                <option>General Inquiry</option>
                <option>Custom Proposal Request</option>
                <option>Grant Evaluation Plan</option>
                <option>Partnership</option>
                <option>Urgent: Grant Deadline</option>
              </select>
            </div>
            <div>
              <label className="overline mb-2 block">Message</label>
              <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-dark" rows={6} data-testid="contact-message" />
            </div>
            <AcceptTerms checked={acceptedTerms} onChange={setAcceptedTerms} testId="contact-accept-terms" />
            {error && <div className="text-sm text-[var(--red)]" data-testid="contact-error">{error}</div>}
            {done && (
              <div className="flex items-center gap-2 text-sm text-emerald-400" data-testid="contact-success">
                <CheckCircle2 className="w-4 h-4" /> Thanks — we&apos;ll get back to you within 1 business day.
              </div>
            )}
            <button type="submit" disabled={submitting} className="btn-red" data-testid="contact-submit">
              {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
